import { type InputHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { cn } from '@/utils/cn';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
};

function inputClass(error?: string): string {
  return cn(
    'w-full cursor-text rounded-[10px] border bg-surface h-[34px] px-3 text-xs text-text-primary',
    'placeholder:text-text-tertiary',
    'transition-colors duration-200',
    'hover:border-border-default',
    'focus:outline-none focus:ring-2 focus:ring-brand-red/30',
    error ? 'border-error' : 'border-border-subtle focus:border-brand-red'
  );
}

function FieldLabel({
  id,
  label,
  required,
}: {
  id: string;
  label: string;
  required: boolean;
}): ReactNode {
  return (
    <label
      htmlFor={id}
      className="mb-1.5 block text-xs font-medium text-text-secondary"
    >
      {label}
      {required && <span className="ml-0.5 text-brand-red">*</span>}
    </label>
  );
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, className, id, required, ...props },
  ref
) {
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-') ?? '';
  return (
    <div>
      {label && <FieldLabel id={inputId} label={label} required={!!required} />}
      <input
        ref={ref}
        id={inputId}
        required={required}
        className={cn(inputClass(error), className)}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  );
});
